import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { useCurrentStateAndParams } from '@wix/tpa-router/react';

import { Divider } from 'wui/Divider';
import { TextField } from 'wui/TextField';
import { DialogTitle } from 'wui/DialogTitle';
import { DialogContent } from 'wui/DialogContent';
import { DialogContainer } from 'wui/DialogContainer';
import { Drawer, type IDrawerProps } from 'wui/Drawer';
import { Search as SearchIcon } from '@wix/wix-ui-icons-common/on-stage';

import { useDidUpdate } from 'common/hooks';

import { SearchResults } from './SearchResults';

interface IProps {
  groupId: string;
}

export function GroupSearch(props: IProps & IDrawerProps) {
  const { groupId, isOpen, onClose, ...rest } = props;

  const { t } = useTranslation();
  const { params, state } = useCurrentStateAndParams();

  const $container = useRef<HTMLDivElement>(null);
  const [query, setQuery] = React.useState<string>();

  const handleQueryChange = useCallback(
    debounce((event: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value);
    }, 500),
    [],
  );

  useEffect(() => {
    if (!isOpen) {
      setQuery(undefined);
    }
  }, [isOpen]);

  // autoclose on navigation
  useDidUpdate(() => {
    onClose();
  }, [params.feedItemId]);

  return (
    <Drawer
      anchor="right"
      isOpen={isOpen}
      onClose={onClose}
      hideCloseButton={false}
      {...rest}
    >
      <DialogContainer>
        <DialogTitle title={t('groups-web.group-search.modal.title')} />

        <Divider bw />

        <DialogContent scrollable={false}>
          <TextField
            autoComplete="off"
            theme="line"
            value={query}
            withClearButton
            prefix={<SearchIcon />}
            onClear={handleReset}
            onChange={handleQueryChange}
            placeholder={t('groups-web.group-search.placeholder')}
          />
        </DialogContent>

        <DialogContent ref={$container} scrollable disableSideGutters>
          <SearchResults
            query={query}
            groupId={groupId}
            onReset={handleReset}
            container={$container.current}
          />
        </DialogContent>
      </DialogContainer>
    </Drawer>
  );

  function handleReset() {
    setQuery(undefined);
  }
}

GroupSearch.displayName = 'GroupSearch';
